/**
 * S14 — Policy Detail Drawer (right-side, 640 px).
 *
 * Surfaces the *full* governance picture for a single policy:
 *   · Intent (why the rule exists)
 *   · Enforcement rules (what ALGOS blocks / logs / throttles)
 *   · Scope (which agents this applies to, derived live from agent.policyIds)
 *   · Frameworks (regulatory mapping)
 *   · Last violation (most recent incident excerpt)
 *   · Version log (every change, who, when)
 *   · Audit excerpt (cross-reference into the audit chain)
 *
 * Authoring / publish workflows are deferred — this drawer is read-only.
 */
import React, { useEffect, useMemo } from "react";
import {
  X,
  ShieldCheck,
  Target,
  ListChecks,
  Layers,
  ScrollText,
  AlertTriangle,
  History,
  FileSignature,
  ArrowUpRight,
} from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { PolicyStatusPill, ViolationsBadge, SeverityDot } from "./components/PolicyPrimitives";

export default function PolicyDetailDrawer({ policy, onClose, agents, auditEntries }) {
  useEffect(() => {
    if (!policy) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [policy, onClose]);

  if (!policy) return null;

  return (
    <div
      className="fixed inset-0 z-50"
      data-testid="policy-detail-drawer"
      role="dialog"
      aria-modal="true"
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-ink-primary/40 backdrop-blur-[2px]"
        data-testid="policy-drawer-backdrop"
      />
      <aside
        className="absolute right-0 top-0 bottom-0 w-full sm:w-[640px] bg-raised shadow-e4 border-l border-border-subtle flex flex-col"
        style={{ animation: "slide-in-right 220ms ease-out" }}
      >
        <DrawerHeader policy={policy} onClose={onClose} />
        <DrawerBody policy={policy} agents={agents} auditEntries={auditEntries} />
        <DrawerFooter onClose={onClose} />
      </aside>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
function DrawerHeader({ policy, onClose }) {
  return (
    <div className="border-b border-border-subtle px-5 pt-4 pb-4" data-testid="policy-drawer-header">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-meta font-mono text-ink-secondary tabular-nums">{policy.id}</span>
            <PolicyStatusPill status={policy.status} />
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-inset text-[10px] font-bold tracking-[0.06em] uppercase text-ink-secondary">
              {policy.category}
            </span>
            <SeverityDot severity={policy.severity} className="mt-0" />
            <span className="text-[10px] font-bold tracking-[0.06em] uppercase text-ink-secondary">
              {policy.severity} severity
            </span>
          </div>
          <h2 className="mt-1 text-h2 text-ink-primary" data-testid="policy-drawer-title">
            {policy.name}
          </h2>
          <p className="mt-1 text-meta text-ink-secondary">
            Owner {policy.owner} · Updated {policy.lastUpdated}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close drawer"
          className="h-8 w-8 inline-flex items-center justify-center rounded-md text-ink-secondary hover:bg-inset transition-colors"
          data-testid="policy-drawer-close"
        >
          <X size={16} />
        </button>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-2 text-center">
        <StatTile label="Agents in scope" value={policy.liveAgentCount ?? policy.appliesTo} tone="tralenex" />
        <StatTile label="Violations" value={policy.violations} tone={policy.violations ? "high" : "low"} />
        <StatTile label="Frameworks" value={(policy.frameworks || []).length} tone="gold" />
      </div>
    </div>
  );
}

function DrawerBody({ policy, agents, auditEntries }) {
  const linkedAgents = useMemo(
    () => agents.filter((a) => (a.policyIds || []).includes(policy.id)),
    [agents, policy.id],
  );
  const auditExcerpt = useMemo(
    () => (auditEntries || []).filter((e) => e.target === policy.id).slice(0, 5),
    [auditEntries, policy.id],
  );

  return (
    <div className="flex-1 overflow-y-auto px-5 py-5 space-y-6" data-testid="policy-drawer-body">
      <Section icon={Target} title="Intent">
        <p
          className="text-[15px] text-ink-primary leading-relaxed"
          data-testid="policy-drawer-intent"
        >
          {policy.intent}
        </p>
      </Section>

      <Section icon={ListChecks} title="Enforcement Rules">
        <ol className="space-y-1.5" data-testid="policy-drawer-rules">
          {(policy.rules || []).map((r, i) => (
            <li key={r} className="flex items-start gap-2.5 text-sm">
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-tralenex-50 text-tralenex-700 text-[10px] font-bold tabular-nums shrink-0 mt-0.5">
                {i + 1}
              </span>
              <span className="text-ink-primary leading-relaxed">{r}</span>
            </li>
          ))}
        </ol>
      </Section>

      <Section icon={Layers} title="Scope">
        <p className="text-ink-primary leading-relaxed" data-testid="policy-drawer-scope">
          {policy.scopeText}
        </p>
        <p className="mt-2 text-meta text-ink-secondary">
          Currently applies to{" "}
          <span className="font-semibold text-ink-primary tabular-nums">{linkedAgents.length}</span>{" "}
          governed agent{linkedAgents.length === 1 ? "" : "s"}.
        </p>
      </Section>

      <Section icon={ShieldCheck} title="Regulatory Frameworks">
        <div className="flex flex-wrap gap-1.5" data-testid="policy-drawer-frameworks">
          {(policy.frameworks || []).map((f) => (
            <span
              key={f}
              className="inline-flex items-center px-2 py-1 rounded bg-tralenex-50 text-[12px] text-tralenex-900 font-medium"
            >
              {f}
            </span>
          ))}
        </div>
      </Section>

      <Section icon={Layers} title="Linked Agents">
        <LinkedAgentsList agents={linkedAgents} />
      </Section>

      <Section icon={AlertTriangle} title="Last Violation">
        <LastViolation violation={policy.lastViolation} />
      </Section>

      <Section icon={History} title="Version Log">
        <VersionLog versions={policy.versionHistory} />
      </Section>

      <Section icon={ScrollText} title="Audit Excerpt">
        <AuditExcerpt entries={auditExcerpt} />
      </Section>
    </div>
  );
}

function DrawerFooter({ onClose }) {
  return (
    <div
      className="border-t border-border-subtle px-5 py-3 bg-inset/30 flex items-center gap-2"
      data-testid="policy-drawer-footer"
    >
      <button
        type="button"
        onClick={onClose}
        className="h-9 px-3 rounded-md text-sm font-medium text-ink-secondary hover:bg-inset transition-colors"
        data-testid="policy-drawer-action-close"
      >
        Close
      </button>
      <span className="flex-1" />
      <span className="inline-flex items-center gap-1.5 text-meta text-ink-disabled">
        <FileSignature size={12} /> Authoring available in a later wave
      </span>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
function StatTile({ label, value, tone }) {
  const toneClass =
    tone === "high"
      ? "text-risk-high"
      : tone === "low"
        ? "text-risk-low"
        : tone === "gold"
          ? "text-gold-600"
          : "text-tralenex-700";
  return (
    <div className="rounded-md bg-inset/60 px-2 py-2">
      <p className="text-[9px] font-bold tracking-[0.1em] uppercase text-ink-secondary">{label}</p>
      <p className={cn("mt-0.5 text-lg font-semibold tabular-nums leading-none", toneClass)}>
        {value}
      </p>
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <section>
      <div className="flex items-center gap-2 mb-1.5">
        <Icon size={13} className="text-tralenex-700" />
        <p className="eyebrow">{title}</p>
      </div>
      <div className="text-sm">{children}</div>
    </section>
  );
}

function LinkedAgentsList({ agents }) {
  if (agents.length === 0) {
    return (
      <p className="text-meta text-ink-secondary" data-testid="policy-drawer-no-linked-agents">
        No governed agents currently inherit this policy.
      </p>
    );
  }
  const preview = agents.slice(0, 8);
  const remaining = agents.length - preview.length;
  return (
    <ul className="divide-y divide-border-subtle/60 rounded-md border border-border-subtle/60 overflow-hidden" data-testid="policy-drawer-linked-agents">
      {preview.map((a) => (
        <li key={a.id} className="px-2.5 py-2 hover:bg-inset/40 transition-colors">
          <Link
            to={`/govern/agents/${a.id}`}
            className="flex items-center justify-between gap-2 text-sm"
            data-testid={`policy-drawer-agent-link-${a.id}`}
          >
            <div className="min-w-0">
              <p className="text-ink-primary truncate">{a.name}</p>
              <p className="text-meta text-ink-secondary font-mono tabular-nums">
                {a.id} · {a.department}
              </p>
            </div>
            <span className="inline-flex items-center gap-1 text-meta text-ink-secondary">
              <span className="uppercase tracking-[0.04em] font-semibold">{a.riskBand}</span>
              <ArrowUpRight size={12} />
            </span>
          </Link>
        </li>
      ))}
      {remaining > 0 && (
        <li className="px-2.5 py-2 text-meta text-ink-secondary bg-inset/30">
          + {remaining} more agent{remaining === 1 ? "" : "s"} in scope
        </li>
      )}
    </ul>
  );
}

function LastViolation({ violation }) {
  if (!violation) {
    return (
      <p className="text-meta text-risk-low" data-testid="policy-drawer-no-violation">
        No violations recorded — policy is enforcing cleanly.
      </p>
    );
  }
  return (
    <div className="rounded-md border border-risk-high/30 bg-risk-high/5 p-3" data-testid="policy-drawer-last-violation">
      <div className="flex items-center justify-between gap-2">
        <span className="text-meta font-mono text-ink-secondary tabular-nums">{violation.ts}</span>
        {violation.agentId && (
          <Link
            to={`/govern/agents/${violation.agentId}`}
            className="inline-flex items-center gap-1 text-meta text-tralenex-700 hover:underline tabular-nums"
            data-testid="policy-drawer-violation-agent-link"
          >
            {violation.agentId} <ArrowUpRight size={11} />
          </Link>
        )}
      </div>
      <p className="mt-1.5 text-sm text-ink-primary leading-relaxed">{violation.summary}</p>
    </div>
  );
}

function VersionLog({ versions }) {
  if (!versions || versions.length === 0) {
    return <p className="text-meta text-ink-secondary">No version history yet.</p>;
  }
  return (
    <ul className="space-y-2" data-testid="policy-drawer-version-log">
      {versions.map((v) => (
        <li key={v.version} className="flex items-start gap-3 text-sm">
          <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-inset text-[10px] font-bold tracking-[0.04em] uppercase text-ink-secondary tabular-nums shrink-0">
            {v.version}
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-ink-primary leading-relaxed">{v.change}</p>
            <p className="text-meta text-ink-secondary mt-0.5">
              {v.ts} · {v.actor}
            </p>
          </div>
        </li>
      ))}
    </ul>
  );
}

function AuditExcerpt({ entries }) {
  if (!entries || entries.length === 0) {
    return (
      <p className="text-meta text-ink-secondary" data-testid="policy-drawer-no-audit">
        No audit entries reference this policy in the visible window.
      </p>
    );
  }
  return (
    <ul className="space-y-1.5" data-testid="policy-drawer-audit">
      {entries.map((e) => (
        <li key={`${e.ts}-${e.action}`} className="flex items-start gap-3 text-sm border-b border-border-subtle/60 pb-1.5 last:border-0">
          <span className="text-meta font-mono text-ink-secondary tabular-nums shrink-0 w-[180px]">
            {e.ts}
          </span>
          <div className="min-w-0">
            <p className="text-ink-primary">
              <span className="font-mono text-ink-secondary">{e.actor}</span>{" "}
              <span className="text-ink-secondary">→</span>{" "}
              <span className="font-semibold">{e.action}</span>
            </p>
          </div>
        </li>
      ))}
    </ul>
  );
}
